// Chat conversation backgrounds — abstract, subtle, theme-adaptive presets.
//
// Four options ship: Plain, Dot grid, Diagonal weave, Grain haze. Styles are
// built with color-mix over palette tokens (var(--n-ink) / var(--n-canvas)) so
// they adapt automatically to light & dark and never use raw colours. The
// pattern sits behind the message list in the shared ThreadView, so the chosen
// background shows identically on desktop (docked/undocked) and mobile.
//
// Preferences (per-user, this device):
//   • chat_bg_default            — the user's default background key
//   • chat_bg_map (JSON)         — { [conversationId]: key } per-conversation overrides
// A conversation with no override falls back to the user's default.

const ink = (pct) => `color-mix(in srgb, var(--n-ink) ${pct}%, transparent)`;

// Very fine film-grain tile (SVG turbulence, no binary asset).
const GRAIN = "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.5'/%3E%3C/svg%3E\")";

export const CHAT_BG_OPTIONS = [
  { key: 'plain', label: 'Plain' },
  { key: 'dots', label: 'Dot grid' },
  { key: 'diagonal', label: 'Diagonal' },
  { key: 'grain', label: 'Grain haze' },
];

export const CHAT_BG_KEYS = CHAT_BG_OPTIONS.map((o) => o.key);

// Returns a React style object for a background key (used behind the messages).
export function chatBgStyle(key) {
  switch (key) {
    case 'dots':
      return {
        backgroundColor: 'var(--n-canvas)',
        backgroundImage: `radial-gradient(${ink(10)} 1.1px, transparent 1.4px)`,
        backgroundSize: '18px 18px',
      };
    case 'diagonal':
      return {
        backgroundColor: 'var(--n-canvas)',
        backgroundImage: `repeating-linear-gradient(45deg, ${ink(6)} 0, ${ink(6)} 1px, transparent 1px, transparent 12px)`,
      };
    case 'grain':
      return {
        backgroundColor: 'var(--n-canvas)',
        backgroundImage: GRAIN,
        backgroundSize: '140px 140px',
      };
    case 'plain':
    default:
      return { backgroundColor: 'var(--n-canvas)' };
  }
}

// A compact style for the picker swatches (smaller tile for grain/dots).
export function chatBgSwatchStyle(key) {
  const base = chatBgStyle(key);
  if (key === 'grain') return { ...base, backgroundSize: '70px 70px' };
  if (key === 'dots') return { ...base, backgroundSize: '10px 10px' };
  return base;
}

const DEFAULT_KEY = 'chat_bg_default';
const MAP_KEY = 'chat_bg_map';

export function getDefaultBg() {
  try {
    const v = localStorage.getItem(DEFAULT_KEY);
    return CHAT_BG_KEYS.includes(v) ? v : 'plain';
  } catch { return 'plain'; }
}

export function setDefaultBg(key) {
  try { localStorage.setItem(DEFAULT_KEY, CHAT_BG_KEYS.includes(key) ? key : 'plain'); } catch { /* noop */ }
}

export function readBgMap() {
  try { return JSON.parse(localStorage.getItem(MAP_KEY) || '{}') || {}; }
  catch { return {}; }
}

export function writeBgMap(map) {
  try { localStorage.setItem(MAP_KEY, JSON.stringify(map || {})); } catch { /* noop */ }
}
